import { useEffect, useRef } from 'react';

import { CommonApplicationServices } from '@provider-portal/common/services/CommonApplicationServices';
import { useTargetingParameter } from '@provider-portal/common/services/TargetingService';
import type { Nullable } from '@provider-portal/common/types/types';

import { getAppLocale } from 'utils/translations';
import { SelectedProvider } from '@provider-portal/common/services/types/account';

export function useBrazeChangeUserOnMount(
  commonAppServices: CommonApplicationServices,
  selectedProvider: Nullable<SelectedProvider>,
  isTargetingParametersRequestFinished: boolean,
) {
  const hasCalledRef = useRef(false);
  const isBrazeWebSdkEnabled = useTargetingParameter(
    'enable_braze_web_sdk',
    commonAppServices.targetingService,
    Boolean,
  );

  // changing the provider Id triggers a new braze session (changeUser) and then a full page reload
  // (app/src/rdx/account/epics.ts:166). The full page reload leaves the braze requests in the air.
  // For this reason, we call changeUser only on mount (after reload). To be updated after we fix
  // the full page reload.
  useEffect(() => {
    if (
      hasCalledRef.current ||
      !isTargetingParametersRequestFinished ||
      !selectedProvider?.providerId
    ) {
      return;
    }

    if (!isBrazeWebSdkEnabled) {
      return;
    }

    const { canSeePromotions, canSeeSmartPromotions, canCreatePaidVisibility } =
      selectedProvider.permissions;

    commonAppServices.brazeService.changeUser(selectedProvider.providerId);
    commonAppServices.brazeService.setLanguage(getAppLocale());
    commonAppServices.brazeService.setHasSponsoredListingsAccess(
      (canSeePromotions || canSeeSmartPromotions) && canCreatePaidVisibility,
    );
    hasCalledRef.current = true;
  }, [
    commonAppServices,
    selectedProvider,
    isTargetingParametersRequestFinished,
    isBrazeWebSdkEnabled,
  ]);
}
